// Vendors
import { createApp } from 'vue';
import Axios from 'axios';
import DOMPurify from 'dompurify';
import VueAxios from 'vue-axios';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { SwatchesPicker } from 'vue-color';
import 'vue-color/style.css';
import { Form as ValidationObserver, Field as ValidationProvider, defineRule, configure } from 'vee-validate';
import vSelect from 'vue-select';
import { mask } from 'vue-the-mask';
import ShardsVue from 'shards-vue3';

// ShardsVue3 textarea patch (removes Vue3 proxy warnings: `el`, `ariaInvalid`)
import PatchedFormTextarea from '@/components/shards/FormTextarea.vue';

// Styles
import 'bootstrap/dist/css/bootstrap.css';
import 'shards-ui/dist/css/shards.css';
import '@/scss/shards-dashboards.scss';
import 'vue-select/dist/vue-select.css';
import '@/assets/scss/vue-select.override.scss';

// Core
import App from './App.vue';
// import AppConfig from './.env';
import AppConfig from './config.js';
import router from './router';
import store from './store';
import SanitizeUtils from './utils/SanitizeUtils';

// Layouts
import Default from '@/layouts/Default.vue';
import GuestLayout from '@/layouts/Guest.vue';

// Helpers
import addVeeValidateRules from '@/utils/VeeValidateRules.js';

// Custom components
import FileInput from '@/components/common/FileInput.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import UserData from '@/components/common/UserData.vue';
import SortableTableHeading from '@/components/common/SortableTableHeading.vue';

// Create app instance
const app = createApp(App);

// Global components
app.component('default-layout', Default);
app.component('guest-layout', GuestLayout);
app.component('validation-provider', ValidationProvider);
app.component('validation-observer', ValidationObserver);
app.component('v-select', vSelect);
app.component('file-input', FileInput);
app.component('page-header', PageHeader);
app.component('sortable-table-heading', SortableTableHeading);
app.component('user-data', UserData);
app.component('swatches-picker', SwatchesPicker);

// Global directives
app.directive('mask', mask);

// Configure vendors
addVeeValidateRules(defineRule, configure);

// Axios
window.axios = Axios;
app.config.globalProperties.$http = Axios;
app.config.globalProperties.$http.defaults.baseURL = AppConfig.apiServerUrl;

app.config.globalProperties.$http.defaults.withCredentials = true;

// Konfiguracja CSRF tokenu dla Laravel Sanctum
// Axios będzie szukać XSRF-TOKEN w ciasteczku i wysyłać go w headeru X-XSRF-TOKEN
app.config.globalProperties.$http.defaults.xsrfCookieName = 'XSRF-TOKEN';
app.config.globalProperties.$http.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

app.config.globalProperties.$http.defaults.headers['Cache-Control'] = 'no-store,max-age=0';

app.config.globalProperties.$http.interceptors.response.use(undefined, (error) => {
  if (error.response.status === 401 && router.currentRoute.value.path !== '/logowanie') {
    store.commit('user/logoutUser');
    router.push('/login');
  }

  return Promise.reject(error);
});

// Event bus for Vue 3 (using mitt or similar would be better, but keeping simple emitter for now)
import mitt from 'mitt';
const emitter = mitt();
app.config.globalProperties.$bus = emitter;

// Other
app.use(VueAxios, Axios);
app.use(VueSweetalert2);
app.use(ShardsVue);

// Override buggy shards-vue3 textarea implementation
app.component('dFormTextarea', PatchedFormTextarea);
app.component('dTextarea', PatchedFormTextarea);

// DOMPurify configuration
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
  }
});

// Register v-pure-html directive
app.directive('pure-html', {
  mounted: (el, binding) => {
    el.innerHTML = DOMPurify.sanitize(binding.value);
  },
  updated: (el, binding) => {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value);
  }
});

// Check auth on app start
const token = localStorage.getItem('token');
if (token) {
  app.config.globalProperties.$http.defaults.headers.Authorization = `Bearer ${token}`;
}
await store.dispatch('user/checkAuth');

// Use router and store
app.use(router);
app.use(store);

// Mount app
app.mount('#app');
